"use client";

import { Plus } from "@/components/icons";
import { Button } from "@/components/ui/button";
import type { TruckLoadingProductSlug } from "@/lib/calculators/truck-loading-merged/product-presets";
import { useToolComputed } from "@/hooks/use-tool-computed";
import { useCalculatorState } from "./use-calculator-state";
import { ProductTypeSelector } from "./product-type-selector";
import { TruckSelector } from "./truck-selector";
import { DimensionInputs } from "./dimension-inputs";
import { LoadingTypeSelector } from "./loading-type-selector";
import { Results } from "./results";
import { FitSceneDynamic } from "./fit-scene-dynamic";
import { AdditionalItemRow } from "./additional-item-row";
import {
  CalculatorSection,
  CalculatorWorkbench,
} from "@/components/calculators/shared/calculator-workbench";

interface TruckLoadingCalculatorProps {
  productType: TruckLoadingProductSlug;
  basePath: string;
}

export function TruckLoadingCalculator({
  productType,
  basePath,
}: TruckLoadingCalculatorProps) {
  const state = useCalculatorState(productType);
  const hasDimensions = state.item.lengthIn > 0 && state.item.widthIn > 0 && state.item.heightIn > 0;
  useToolComputed(
    "truck-loading",
    hasDimensions ? JSON.stringify({
      truck: state.selectedTruck.id,
      item: state.item,
      loadingType: state.loadingType,
      targetQty: state.specificQtyEnabled ? state.targetQty : null,
      additionalItems: state.additionalItems,
      perFloor: state.perFloor,
      stackLayers: state.stackLayers,
    }) : null,
    productType,
  );

  return (
    <div className="space-y-6">
      <CalculatorWorkbench
        resultsLabel={`Load · ${state.selectedTruck.label}`}
        inputs={
          <>
            <ProductTypeSelector activeSlug={productType} basePath={basePath} />
            <CalculatorSection title="Truck">
              <TruckSelector
                selectedTruckId={state.selectedTruck.id}
                onSelect={state.setSelectedTruck}
              />
            </CalculatorSection>
            <DimensionInputs
              config={state.config}
              presetId={state.presetId}
              geometryCategory={state.geometryCategory}
              length={state.length}
              width={state.width}
              height={state.height}
              weight={state.weight}
              palletized={state.palletized}
              palletHeight={state.palletHeight}
              stackable={state.stackable}
              onStackableChange={state.setStackable}
              specificQtyEnabled={state.specificQtyEnabled}
              targetQty={state.targetQty}
              onPresetChange={state.handlePresetChange}
              onGeometryCategoryChange={state.setGeometryCategory}
              onLengthChange={state.handleDimensionChange.length}
              onWidthChange={state.handleDimensionChange.width}
              onHeightChange={state.handleDimensionChange.height}
              onWeightChange={state.setWeight}
              onPalletizedChange={state.setPalletized}
              onPalletHeightChange={state.setPalletHeight}
              onSpecificQtyEnabledChange={state.setSpecificQtyEnabled}
              onTargetQtyChange={state.setTargetQty}
            />
            <LoadingTypeSelector
              value={state.loadingType}
              onChange={state.setLoadingType}
              truck={state.virtualTruck}
              item={state.item}
              bestType={state.bestLoadingType}
              specificQtyEnabled={state.specificQtyEnabled}
              parsedTargetQty={Math.max(
                Math.round(Number.parseFloat(state.targetQty) || 0),
                0,
              )}
            />
            <CalculatorSection
              title="Mixed load"
              action={
                <Button variant="outline" size="sm" type="button" onClick={state.addItem}>
                  <Plus className="h-4 w-4" />
                  Add product
                </Button>
              }
            >
              {state.additionalItems.length > 0 && (
                <div className="space-y-2">
                  {state.additionalItems.map((addedItem) => (
                    <AdditionalItemRow
                      key={addedItem.id}
                      item={addedItem}
                      onUpdate={state.updateItem}
                      onRemove={state.removeItem}
                    />
                  ))}
                </div>
              )}
            </CalculatorSection>
          </>
        }
        results={
          <Results
            truck={state.selectedTruck}
            result={state.result}
            hasWeight={state.hasWeight}
            perFloor={state.perFloor}
            stackLayers={state.stackLayers}
            singularLabel={state.unitLabels.singular}
            pluralLabel={state.unitLabels.plural}
            specificQtyEnabled={state.specificQtyEnabled}
            targetQty={state.targetQty}
            additionalItems={state.additionalItems}
            onPerFloorChange={state.setPerFloor}
            onStackLayersChange={state.setStackLayers}
          />
        }
      />

      <FitSceneDynamic
        truck={state.selectedTruck}
        item={state.item}
        positions={state.positions}
        baseColor={state.config.baseColor}
      />
    </div>
  );
}
